"use client";

import React, { useEffect, useMemo, useCallback } from "react";
import { useParams } from "next/navigation";
import { useHeader } from "@/context/HeaderLayoutContext";
import { useFetchEcompage } from "@/apis/queryFunctions/ecompagesFunctions";
import { parseHtmlJson } from "@/utils/parseHtmlTextToJson";
import appendImageUrlFromAPI from "@/utils/appendImageUrlFromAPI";

// Section Components
import HeroSection, { HeroStat } from "./components/HeroSection";
import ProductionSection from "./components/ProductionSection";
import FactorySection from "./components/FactorySection";
import ProductSection from "./components/ProductSection";
import CommitmentSection from "./components/CommitmentSection";
import PackagingSection from "./components/PackagingSection";
import PaymentSection from "./components/PaymentSection";
import PriceSection from "./components/PriceSection";

// Styles
import styles from "./export.module.css";

export default function ExportPage() {
  const params = useParams();
  const locale = (params?.locale as "vi" | "en") || "vi";
  const { setHeaderStyle } = useHeader();

  // 1. Fetch API data from slug "trang-xuat-khau-website-viet-thien"
  const { data: pageData } = useFetchEcompage(
    "trang-xuat-khau-website-viet-thien",
    locale
  );

  // Set header style transparent on page load (top of page is transparent, green on scroll)
  useEffect(() => {
    setHeaderStyle({ transparent: true });
    return () => setHeaderStyle({ transparent: false });
  }, [setHeaderStyle]);

  const actualPageData = pageData?.root || pageData;

  const allAddOn1 = useMemo(() => {
    return Array.isArray(actualPageData?.add_on_data) &&
      actualPageData.add_on_data.length > 0
      ? actualPageData.add_on_data
      : [];
  }, [actualPageData]);

  const allAddOn2 = useMemo(() => {
    return Array.isArray(actualPageData?.add_on_data_2) &&
      actualPageData.add_on_data_2.length > 0
      ? actualPageData.add_on_data_2
      : [];
  }, [actualPageData]);

  // #region Helpers: Tìm addon và kiểm tra share / index (theo HomeClient)
  const getAddOn1 = useCallback(
    (keys: string[]) => {
      const lowerKeys = keys.map((k) => k.toLowerCase());
      return (
        allAddOn1.find((item: any) => {
          const title = (item?.title || "").toLowerCase();
          const summary = (item?.summary || "").toLowerCase();
          return lowerKeys.some(
            (k) => title === k || summary === k || title.includes(k)
          );
        }) || null
      );
    },
    [allAddOn1]
  );

  const isSectionShared = useCallback(
    (keys: string[], defaultVal = true): boolean => {
      const item = getAddOn1(keys);
      if (!item) return false;
      return (
        item.shared !== false &&
        item.shared !== "false" &&
        item.shared !== 0 &&
        item.shared !== "0"
      );
    },
    [getAddOn1]
  );

  const getSectionIndex = useCallback(
    (keys: string[], defaultVal: number): number => {
      const addon = getAddOn1(keys);
      if (!addon) return defaultVal;
      const stt = addon.stt;
      if (stt === undefined || stt === null || stt === "") {
        return defaultVal;
      }
      const val = Number(stt);
      return isNaN(val) ? defaultVal : val;
    },
    [getAddOn1]
  );
  // #endregion

  // ================= 1. Hero Banner =================
  const heroData = useMemo(() => {
    const bannerItem = getAddOn1(["banner"]);
    const bannerJson = bannerItem?.content
      ? parseHtmlJson(bannerItem.content)
      : null;

    if (!bannerJson?.title && !bannerItem && !actualPageData?.title) {
      return null;
    }

    const bgImage = bannerItem?.picture
      ? appendImageUrlFromAPI({ src: bannerItem.picture, size: "2k" })
      : actualPageData?.picture
      ? appendImageUrlFromAPI({ src: actualPageData.picture, size: "2k" })
      : "";

    const stats: HeroStat[] =
      Array.isArray(bannerJson?.stats) && bannerJson.stats.length > 0
        ? bannerJson.stats
        : [
            {
              num: "16+",
              unit: "Năm kinh nghiệm",
              label: "Sản xuất & xuất khẩu",
              icon: "Award",
            },
            {
              num: "230,000",
              unit: "Tấn cà phê / năm",
              label: "Cung cấp thị trường",
              icon: "Package",
            },
            {
              num: "2,000+",
              unit: "Khách hàng quốc tế",
              label: "Tại hơn 20 quốc gia",
              icon: "Globe",
            },
          ];

    return {
      title:
        bannerJson?.title ||
        actualPageData?.title ||
        "XUẤT KHẨU CÀ PHÊ KHẮP THẾ GIỚI",
      description:
        bannerJson?.description ||
        actualPageData?.description ||
        "Với hơn 16 năm kinh nghiệm trong sản xuất, chế biến và xuất khẩu cà phê, Việt Thiên đã cung cấp trung bình 230,000 tấn cà phê mỗi năm cho hơn 5,000 khách hàng trong nước và hơn 2,000 khách hàng quốc tế tại hơn 20 quốc gia và vùng lãnh thổ.",
      bgImage,
      stats,
    };
  }, [getAddOn1, actualPageData]);

  // ================= 2. Về sản xuất cà phê nhân xanh =================
  const productionData = useMemo(() => {
    const prodItem = getAddOn1(["nhan-xanh"]);
    if (!prodItem) return null;

    const prodJson = prodItem?.content ? parseHtmlJson(prodItem.content) : null;
    const images = [
      prodItem?.picture
        ? appendImageUrlFromAPI({ src: prodItem.picture, size: "2k" })
        : "",
      prodItem?.picture_2
        ? appendImageUrlFromAPI({ src: prodItem.picture_2, size: "2k" })
        : "",
      prodItem?.picture_3
        ? appendImageUrlFromAPI({ src: prodItem.picture_3, size: "2k" })
        : "",
    ].filter(Boolean);

    return {
      subtitle: prodItem?.summary || "NGUỒN NGUYÊN LIỆU & QUY TRÌNH",
      title: prodJson?.title || prodItem?.title || "VỀ SẢN XUẤT CÀ PHÊ NHÂN XANH",
      description: prodJson?.description || prodItem?.content_2 || "",
      images,
    };
  }, [getAddOn1]);

  // ================= 3. Hệ thống nhà máy =================
  const factoriesData = useMemo(() => {
    const headerItem = getAddOn1(["nha-may"]);
    const nhaMayJson = headerItem?.content
      ? parseHtmlJson(headerItem.content)
      : null;

    const factoryItems = allAddOn2.filter((item: any) => {
      const title = (item?.title || "").toLowerCase();
      return title === "nha-may" || title.includes("nha-may");
    });

    if (factoryItems.length === 0 && !headerItem) return null;

    const parsedFactories = factoryItems.map((item: any, idx: number) => {
      const parsed = item?.content ? parseHtmlJson(item.content) : null;
      const images = [
        item?.picture
          ? appendImageUrlFromAPI({ src: item.picture, size: "l" })
          : "",
        item?.picture_2
          ? appendImageUrlFromAPI({ src: item.picture_2, size: "l" })
          : "",
        item?.picture_3
          ? appendImageUrlFromAPI({ src: item.picture_3, size: "l" })
          : "",
        item?.picture_4
          ? appendImageUrlFromAPI({ src: item.picture_4, size: "l" })
          : "",
      ].filter(Boolean);

      return {
        num: `0${idx + 1}`,
        name: parsed?.name || item?.content_3 || `Nhà Máy ${idx + 1}`,
        address: parsed?.address || "",
        area: parsed?.area || "",
        capacity: parsed?.capacity || "",
        images,
      };
    });

    return {
      subtitle: headerItem?.summary || "CƠ SỞ HẠ TẦNG & NĂNG LỰC CHẾ BIẾN",
      title: nhaMayJson?.title || "HỆ THỐNG 3 NHÀ MÁY SẢN XUẤT",
      items: parsedFactories,
    };
  }, [getAddOn1, allAddOn2]);

  // ================= 4. Sản phẩm cà phê =================
  const productsData = useMemo(() => {
    const headerItem = getAddOn1(["ca-phe-xuat-khau", "ca-phe"]);

    const productCategories = allAddOn2.filter((item: any) => {
      const title = (item?.title || "").toLowerCase();
      return title === "ca-phe-xuat-khau" || title.includes("ca-phe");
    });

    if (!headerItem && productCategories.length === 0) return null;

    const buildCategorySpecs = (catId: string) => {
      const items = productCategories.filter((item: any) => {
        const summary = (item?.summary || "").toLowerCase();
        return summary === catId || summary.includes(catId);
      });

      const specs = items.map((item: any) => {
        const label = item?.content_2 || "";
        const parsedVals = item?.content ? parseHtmlJson(item.content) : [];
        const pics = [
          item?.picture,
          item?.picture_2,
          item?.picture_3,
          item?.picture_4,
        ]
          .filter(Boolean)
          .map((src: string) => appendImageUrlFromAPI({ src, size: "l" }));

        const valStr = Array.isArray(parsedVals)
          ? parsedVals.map((v: any) => v?.label || "").join(", ")
          : "";

        return { label, value: valStr, images: pics };
      });

      const defaultImg = items[0]?.picture
        ? appendImageUrlFromAPI({ src: items[0].picture, size: "l" })
        : "";

      return { specs, defaultImg };
    };

    const robusta = buildCategorySpecs("robusta");
    const arabica = buildCategorySpecs("arabica");
    const excelsa = buildCategorySpecs("excelsa");

    return {
      subtitle: headerItem?.summary || "DANH MỤC XUẤT KHẨU",
      title: headerItem?.summary || "SẢN PHẨM CÀ PHÊ VIỆT THIÊN",
      robustaTitle: "Cà phê nhân xanh Robusta Việt Nam",
      robustaSpecs: robusta.specs,
      robustaDefaultImg: robusta.defaultImg,
      arabicaTitle: "Cà phê nhân xanh Arabica Việt Nam",
      arabicaSpecs: arabica.specs,
      arabicaDefaultImg: arabica.defaultImg,
      excelsaTitle: "Cà phê nhân xanh Excelsa Việt Nam",
      excelsaSpecs: excelsa.specs,
      excelsaDefaultImg: excelsa.defaultImg,
    };
  }, [getAddOn1, allAddOn2]);

  // ================= 5. Tiêu chuẩn xuất khẩu =================
  const standardsData = useMemo(() => {
    const stdItem = getAddOn1(["tieu-chuan"]);
    if (!stdItem) return null;

    const stdJson = stdItem?.content ? parseHtmlJson(stdItem.content) : null;

    const certItems = allAddOn2.filter((item: any) => {
      const title = (item?.title || "").toLowerCase();
      return title === "tieu-chuan" || title.includes("tieu-chuan");
    });

    const certPics = certItems
      .flatMap((item: any) => [
        item?.picture,
        item?.picture_2,
        item?.picture_3,
        item?.picture_4,
        item?.picture_5,
        item?.picture_6,
      ])
      .filter(Boolean)
      .map((src: string) => ({
        name: "Cert",
        src: appendImageUrlFromAPI({ src, size: "l" }),
      }));

    return {
      subtitle: stdItem?.summary || "TIÊU CHUẨN XUẤT KHẨU & CAM KẾT CHẤT LƯỢNG",
      title:
        stdJson?.title ||
        stdItem?.summary ||
        "TIÊU CHUẨN XUẤT KHẨU & CAM KẾT CHẤT LƯỢNG",
      qcTitle: stdJson?.qcTitle,
      qcDesc: stdJson?.qcDesc,
      qcBullets: stdJson?.qcBullets || [],
      certTitle: stdJson?.certTitle,
      certDesc: stdJson?.certDesc,
      certs: certPics,
    };
  }, [getAddOn1, allAddOn2]);

  // ================= 6. Quy cách đóng gói =================
  const packagingData = useMemo(() => {
    const pkgItem = getAddOn1(["quy-cach-dong-goi", "dong-goi"]);
    if (!pkgItem) return null;

    const pkgJson = pkgItem?.content ? parseHtmlJson(pkgItem.content) : null;

    const pics = [
      pkgItem?.picture
        ? appendImageUrlFromAPI({ src: pkgItem.picture, size: "l" })
        : "",
      pkgItem?.picture_2
        ? appendImageUrlFromAPI({ src: pkgItem.picture_2, size: "l" })
        : "",
      pkgItem?.picture_3
        ? appendImageUrlFromAPI({ src: pkgItem.picture_3, size: "l" })
        : "",
      pkgItem?.picture_4
        ? appendImageUrlFromAPI({ src: pkgItem.picture_4, size: "l" })
        : "",
    ];

    const rawItems = Array.isArray(pkgJson?.items) ? pkgJson.items : [];
    const items = rawItems.map((item: any, idx: number) => ({
      title: item?.title || "",
      text: item?.text || "",
      img: pics[idx] || "",
    }));

    return {
      subtitle: pkgItem?.summary || "TIÊU CHUẨN ĐÓNG GÓI",
      title: pkgJson?.title || "QUY CÁCH ĐÓNG GÓI",
      description: pkgJson?.description || "",
      items,
    };
  }, [getAddOn1]);

  // ================= 7. Thanh toán & Giao hàng =================
  const tradeData = useMemo(() => {
    const tradeItem = getAddOn1(["thanh-toan"]);
    if (!tradeItem) return null;

    const tradeJson = tradeItem?.content
      ? parseHtmlJson(tradeItem.content)
      : null;

    return {
      subtitle: tradeItem?.summary || "THANH TOÁN VÀ GIAO HÀNG",
      title: tradeJson?.title || "VỀ THANH TOÁN & GIAO HÀNG",
      paymentTitle: tradeJson?.paymentTitle,
      ttTitle: tradeJson?.ttTitle,
      ttDesc: tradeJson?.ttDesc,
      lcTitle: tradeJson?.lcTitle,
      lcDesc: tradeJson?.lcDesc,
      prodTimeTitle: tradeJson?.prodTimeTitle,
      prodTimeVal: tradeJson?.prodTimeVal,
      deliveryTimesTitle: tradeJson?.deliveryTimesTitle,
      deliveryTimes: tradeJson?.deliveryTimes || [],
    };
  }, [getAddOn1]);

  // ================= 8. Giá cà phê trực tuyến =================
  const priceData = useMemo(() => {
    const priceItem = getAddOn1(["gia-ca-phe"]);
    if (!priceItem) return null;

    const priceJson = priceItem?.content
      ? parseHtmlJson(priceItem.content)
      : null;

    return {
      subtitle: priceItem?.summary || "THỊ TRƯỜNG CÀ PHÊ THẾ GIỚI",
      title: priceJson?.title || "GIÁ CÀ PHÊ TRỰC TUYẾN HÔM NAY",
      description: priceJson?.description || "",
    };
  }, [getAddOn1]);

  // #region Danh sách các section và logic cấu hình theo HomeClient
  const sections = useMemo(() => {
    const list: Array<{
      id: string;
      index: number;
      render: () => React.ReactNode;
    }> = [];

    // 1. BANNER SECTION (Hero & Stats)
    if (isSectionShared(["banner"], true)) {
      list.push({
        id: "hero",
        index: getSectionIndex(["banner"], 1),
        render: () => (
          <HeroSection data={heroData || undefined} locale={locale} />
        ),
      });
    }

    // 2. PRODUCTION SECTION (Về sản xuất cà phê nhân xanh)
    if (isSectionShared(["nhan-xanh"], true)) {
      list.push({
        id: "production",
        index: getSectionIndex(["nhan-xanh"], 2),
        render: () => <ProductionSection data={productionData || undefined} />,
      });
    }

    // 3. FACTORIES SECTION (Hệ thống nhà máy)
    if (isSectionShared(["nha-may"], true)) {
      list.push({
        id: "factories",
        index: getSectionIndex(["nha-may"], 3),
        render: () => (
          <FactorySection data={factoriesData || undefined} locale={locale} />
        ),
      });
    }

    // 4. PRODUCTS SECTION (Cà phê xuất khẩu: Robusta, Arabica, Excelsa)
    if (isSectionShared(["ca-phe-xuat-khau", "ca-phe"], true)) {
      list.push({
        id: "products",
        index: getSectionIndex(["ca-phe-xuat-khau", "ca-phe"], 4),
        render: () => <ProductSection data={productsData || undefined} />,
      });
    }

    // 5. STANDARDS SECTION (Tiêu chuẩn xuất khẩu & Cam kết chất lượng)
    if (isSectionShared(["tieu-chuan"], true)) {
      list.push({
        id: "standards",
        index: getSectionIndex(["tieu-chuan"], 5),
        render: () => (
          <CommitmentSection data={standardsData || undefined} />
        ),
      });
    }

    // 6. PACKAGING SECTION (Quy cách đóng gói)
    if (isSectionShared(["quy-cach-dong-goi", "dong-goi"], true)) {
      list.push({
        id: "packaging",
        index: getSectionIndex(["quy-cach-dong-goi", "dong-goi"], 6),
        render: () => (
          <PackagingSection data={packagingData || undefined} />
        ),
      });
    }

    // 7. PAYMENT & DELIVERY SECTION (Thanh toán & Giao hàng)
    if (isSectionShared(["thanh-toan"], true)) {
      list.push({
        id: "trade",
        index: getSectionIndex(["thanh-toan"], 7),
        render: () => <PaymentSection data={tradeData || undefined} />,
      });
    }

    // 8. PRICE SECTION (Giá cà phê trực tuyến)
    if (isSectionShared(["gia-ca-phe"], true)) {
      list.push({
        id: "price",
        index: getSectionIndex(["gia-ca-phe"], 8),
        render: () => (
          <PriceSection data={priceData || undefined} locale={locale} />
        ),
      });
    }

    // Sắp xếp thứ tự section theo thuộc tính index (stt trên CMS)
    return list.sort((a, b) => a.index - b.index);
  }, [
    isSectionShared,
    getSectionIndex,
    heroData,
    productionData,
    factoriesData,
    productsData,
    standardsData,
    packagingData,
    tradeData,
    priceData,
    locale,
  ]);
  // #endregion

  return (
    <div className={styles.wrapper}>
      {sections.map((section) => (
        <React.Fragment key={section.id}>{section.render()}</React.Fragment>
      ))}
    </div>
  );
}
